import type { IconName } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import type { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { isAgentProfileEnabled } from "@/appComponents/Blocks/Cards/ProfileBaseballCard/Agent/AgentProfile.constants";

export type RecordLabel = "Employee" | "Position";

export const AGENT_PROFILE_ICON: IconName = "robot";

// TODO(VIZ-6): refine with guardian checks
export const isAgentRecord = (dataObj?: QObjDBObject | null): boolean =>
  dataObj?.SmartGetFieldValue?.("isAgent") === "true";

export type RecordOptionsPermissions = {
  canAccessProfileCard: boolean;
  canEditDocuments: boolean;
  canAccessRecordDetails: boolean;
  canViewRecordSourceDetails: boolean;
};

export type RecordOption = {
  label: string;
  dataTestId: string;
  value: "doprofile" | "Record Details" | "Source Info";
  icon: IconName;
  clickHandler: () => void;
};

export type GetRecordOptionsParams = {
  isCustomChart: boolean;
  isPositionCustomChart: boolean;
  readOnly: boolean;
  recordLabel: RecordLabel;
  dataObj?: QObjDBObject | null;
  permissions: RecordOptionsPermissions;
  onCommand: (cmd: string) => () => void;
};

export function getRecordOptions(params: GetRecordOptionsParams): RecordOption[] {
  const {
    isCustomChart,
    isPositionCustomChart,
    readOnly,
    recordLabel,
    dataObj,
    permissions,
    onCommand
  } = params;
  const {
    canAccessProfileCard,
    canEditDocuments,
    canAccessRecordDetails,
    canViewRecordSourceDetails
  } = permissions;
  const options: RecordOption[] = [];

  if (canAccessProfileCard) {
    const isAgentBox = isAgentProfileEnabled() && !!dataObj?.IsAgentObj();
    options.push({
      label: appstr(isPositionCustomChart ? `${recordLabel} profile` : "Profile"),
      dataTestId: "ctx-menu-profile",
      value: "doprofile",
      icon: isAgentBox ? AGENT_PROFILE_ICON : "address-card",
      clickHandler: onCommand("doprofile")
    });
  }

  if (canEditDocuments) {
    if (canAccessRecordDetails) {
      let detailsString: string;
      const recordLabelLower = recordLabel.toLowerCase();
      const isEditingCustomChart = isCustomChart && !readOnly;

      if (isPositionCustomChart) {
        detailsString = appstr(
          readOnly ? `${recordLabel} details` : `Edit ${recordLabelLower} details`
        );
      } else if (isEditingCustomChart) {
        detailsString = appstr("Edit record details");
      } else {
        detailsString = appstr("Record details");
      }

      options.push({
        label: detailsString,
        dataTestId: "ctx-menu-record-details",
        value: "Record Details",
        icon: isEditingCustomChart ? "pen-to-square" : "circle-info",
        clickHandler: onCommand("Record Details")
      });
    }

    if (canViewRecordSourceDetails) {
      options.push({
        label: appstr("Source info"),
        dataTestId: "ctx-menu-box-source-details",
        value: "Source Info",
        icon: "database",
        clickHandler: onCommand("Source Info")
      });
    }
  }

  return options;
}
